import { DateTime } from "luxon";
import { RiskRating } from "../../../../../graphql/supergraphEnums";
import { RiskRating as RiskRatingType } from "../../../../../graphql/supergraphTypes";
import { programDisplayName } from "../../../../../utils/compliance_program";
import { daysFromToday, parseLocalDate } from "../../../../../utils/date_formatter";
import { formatCountText } from "../../../../../utils/text_utils";
import { shouldShowRiskRating } from "../../../../riskRating/utils";
import type { RefreshStatusData } from "./types";

const createExpiryStatusField = (
  dateStr: string | null | undefined,
): RefreshStatusData | undefined => {
  if (!dateStr) return { hasIssue: false };

  const days = daysFromToday(dateStr);

  if (days < 0) {
    return {
      hasIssue: true,
      expiryInfo: {
        variant: "warning",
        value: `Expired ${formatCountText(Math.abs(days), "day")} ago`,
      },
    };
  }

  if (days === 0) {
    return {
      hasIssue: false,
      expiryInfo: { variant: "neutral", value: "Expires today" },
    };
  }

  return {
    hasIssue: false,
    expiryInfo: {
      variant: "neutral",
      value: `Expires in ${formatCountText(days, "day")}`,
    },
  };
};

// Uses local midnight parsing to avoid UTC timezone shifts
const findEarliestDocumentExpiry = (
  schedules: Array<{ expiryDate: string | null | undefined }>,
): string | null => {
  const validDates = schedules
    .map((s) => s.expiryDate)
    .filter((d): d is string => d != null && d != undefined)
    .map((d) => parseLocalDate(d))
    .sort((a, b) => a.getTime() - b.getTime());

  return validDates.length > 0 ? validDates[0].toISOString() : null;
};

// Applies the backend `hasActiveEscalation` flag as a post-step override: if
// true, always surface "Issue detected" regardless of the underlying expiry date.
const applyEscalationOverride = (
  data: RefreshStatusData | undefined,
  hasActiveEscalation: boolean | null | undefined,
): RefreshStatusData | undefined => {
  if (hasActiveEscalation === true) {
    return {
      hasIssue: true,
      expiryInfo: data?.expiryInfo,
    };
  }
  return data;
};

export const getProfileRefreshStatusData = (
  profileRefresh?: {
    schedule: {
      nextRefreshDueDate: string | null;
    } | null;
    hasActiveEscalation?: boolean | null;
  } | null,
): RefreshStatusData | undefined => {
  return applyEscalationOverride(
    createExpiryStatusField(profileRefresh?.schedule?.nextRefreshDueDate),
    profileRefresh?.hasActiveEscalation,
  );
};

export const getDocumentRefreshStatusData = (
  docRefreshStatus?: {
    schedules: Array<{
      expiryDate: string;
    }>;
    hasActiveEscalation?: boolean | null;
  } | null,
): RefreshStatusData | undefined => {
  const nextDocumentExpiry = findEarliestDocumentExpiry(docRefreshStatus?.schedules ?? []);
  return applyEscalationOverride(
    createExpiryStatusField(nextDocumentExpiry),
    docRefreshStatus?.hasActiveEscalation,
  );
};

export const getDisplayRiskRating = (
  programId?: number,
  caseRiskRating?: RiskRatingType,
): { rating: RiskRatingType; tooltip: string } => {
  if (shouldShowRiskRating(programId)) {
    return {
      rating: caseRiskRating || RiskRating.None,
      tooltip: "Inspect risk rating",
    };
  } else {
    return {
      rating: RiskRating.None,
      tooltip: `Risk rating does not apply to ${programDisplayName(programId)} compliance program.`,
    };
  }
};

export const getNextCycleCountdown = (): string => {
  const nowIST = DateTime.now().setZone("Asia/Kolkata");
  let nextCycle = nowIST.set({ hour: 13, minute: 0, second: 0, millisecond: 0 });

  if (nowIST >= nextCycle) {
    nextCycle = nextCycle.plus({ days: 1 });
  }

  const diff = nextCycle.diff(nowIST, ["hours", "minutes"]);
  const hours = Math.floor(diff.hours);
  const countdown = hours < 1 ? "In progress" : `Next screening in ${hours} hrs`;

  return countdown;
};
